import { ChevronDown, ChevronRight, Plus } from "lucide-react";
import { GANTT_GROUP_ROW_HEIGHT, GANTT_LABEL_WIDTH, ganttRowPlacement } from "./ganttConstants";

/** A group header spanning the full width, matching the list's group bands. */
export function GanttGroupRow({
  top,
  label,
  count,
  collapsed,
  onToggle,
  width,
  onAdd,
  addLabel,
  dotColor,
  labelWidth = GANTT_LABEL_WIDTH,
}: {
  /** Distance from the top of the chart body. */
  top: number;
  label: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
  width: number;
  /** Opens the New Issue form prefilled from this band. Omitted when the band
   *  has no value to prefill (a date bucket) or the user cannot create. */
  onAdd?: () => void;
  addLabel?: string;
  /** The status's own colour, when the chart is grouped by status. The same
   *  dot the Kanban's column headers carry, so a band and its board column
   *  read as the same thing. */
  dotColor?: string;
  /** The label column's live width — the header and every row must agree. */
  labelWidth?: number;
}) {
  return (
    <div
      className="absolute left-0 top-0 flex border-b bg-muted/30 motion-reduce:!transition-none"
      style={{
        width: labelWidth + width,
        height: GANTT_GROUP_ROW_HEIGHT,
        ...ganttRowPlacement(top),
      }}
    >
      {/* The cell, not a button: it holds two of them. The toggle and the add
          affordance both belong to the band's own column, so they ride the
          horizontal scroll together instead of the add button hanging off the
          side of it. */}
      <div
        className="sticky left-0 z-10 flex shrink-0 items-center gap-1.5 bg-muted/30 pl-3 pr-1"
        style={{ width: labelWidth }}
      >
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left text-xs font-medium"
        >
          {collapsed ? (
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )}
          {dotColor && (
            <span
              aria-hidden="true"
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: dotColor }}
            />
          )}
          <span className="truncate">{label}</span>
          <span className="ml-1 shrink-0 rounded-full bg-muted px-1.5 text-[10px] text-muted-foreground">
            {count}
          </span>
        </button>
        {onAdd && (
          <button
            type="button"
            aria-label={addLabel}
            title={addLabel}
            onClick={onAdd}
            className="shrink-0 rounded p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
